import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useNavScroll } from '@/hooks/useScrollAnimation';

const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Products', path: '/products' },
  { label: 'About', path: '/about' },
  { label: 'Gallery', path: '/#gallery' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const scrolled = useNavScroll();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleNavClick = (path: string) => {
    if (path.startsWith('/#')) {
      const id = path.replace('/#', '');
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        }, 300);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }
      setMenuOpen(false);
    }
  };

  const isActive = (path: string) =>
    path.startsWith('/#') ? false : location.pathname === path;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'nav-scrolled' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer flex-shrink-0">
            <img
              src="https://storage.readdy-site.link/project_files/be4287be-1b15-4a9d-9900-25ef163f62d9/9b6be2bb-b0b7-4a08-9842-7d666baf6ae2_hamva-logo--1.png?v=f2299e6ba6265339a88c77116d80f6d6"
              alt="Hamva Pharmaceuticals"
              className="h-20 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link) =>
              link.path.startsWith('/#') ? (
                <button
                  key={link.label}
                  onClick={() => handleNavClick(link.path)}
                  className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 relative group cursor-pointer
                    ${isActive(link.path) ? 'text-accent' : 'text-white/80 hover:text-white'}`}
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-accent group-hover:w-full transition-all duration-300 rounded-full" />
                </button>
              ) : (
                <Link
                  key={link.label}
                  to={link.path}
                  className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 relative group cursor-pointer
                    ${isActive(link.path) ? 'text-accent' : 'text-white/80 hover:text-white'}`}
                >
                  {link.label}
                  <span className={`absolute -bottom-1 left-1/2 -translate-x-1/2 h-0.5 bg-accent rounded-full transition-all duration-300 ${isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'}`} />
                </Link>
              )
            )}
          </div>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="https://wa.me/923007321011"
              target="_blank"
              rel="noopener noreferrer"
              className="wa-btn flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-base" />
              WhatsApp Us
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden flex items-center justify-center w-10 h-10 cursor-pointer text-white"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden transition-all duration-300 overflow-hidden ${
            menuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
          style={{ background: 'rgba(14,18,24,0.97)', backdropFilter: 'blur(20px)' }}
        >
          <div className="px-6 py-4 flex flex-col gap-4 border-t border-white/10">
            {NAV_LINKS.map((link) =>
              link.path.startsWith('/#') ? (
                <button
                  key={link.label}
                  onClick={() => handleNavClick(link.path)}
                  className="text-left text-base font-medium text-white/80 hover:text-white py-2 cursor-pointer whitespace-nowrap"
                >
                  {link.label}
                </button>
              ) : (
                <Link
                  key={link.label}
                  to={link.path}
                  className={`text-base font-medium py-2 whitespace-nowrap ${
                    isActive(link.path) ? 'text-accent' : 'text-white/80 hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
            <a
              href="https://wa.me/923007321011"
              target="_blank"
              rel="noopener noreferrer"
              className="wa-btn flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white mt-2 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line" />
              WhatsApp Us
            </a>
          </div>
        </div>
      </nav>
    </>
  );
}
